import React, { useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import Notification from "../../../component/notification";
import { doc, setDoc, serverTimestamp, deleteDoc, getDocs, collection, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase';
import { v4 as uuidv4 } from 'uuid';

// Initial mock categories for UI fallback
const initialCategories = [
  { name: "Web Development", description: "Courses related to web development technologies" },
  { name: "Mobile App Development", description: "Courses for iOS and Android development" },
  { name: "UI/UX Design", description: "User interface and experience design courses" },
];

const Category = () => {
  // State management
  const [categories, setCategories] = useState(initialCategories);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [categoryName, setCategoryName] = useState("");
  const [description, setDescription] = useState("");
  const [editIndex, setEditIndex] = useState(null);
  const [notification, setNotification] = useState({ visible: false, message: "", type: "" });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const itemsPerPage = 10;

  // Filter and paginate categories
  const filtered = categories.filter(cat => cat && cat.name && cat.name.toLowerCase().includes(search.toLowerCase()));
  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginated = filtered.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  // Handle search input
  const handleSearch = e => {
    setSearch(e.target.value);
    setPage(1);
  };

  // Add or update a category
  const handleAddCategory = async e => {
    e.preventDefault();
    if (!categoryName.trim() || !description.trim()) {
      setNotification({ visible: true, message: "❗Please fill in all fields!", type: "warning" });
      setTimeout(() => setNotification({ visible: false, message: "", type: "" }), 3000);
      return;
    }
    try {
      const oldDocId = editIndex !== null ? categories[editIndex].id : null;
      const newDocId = `${categoryName.trim()}-${uuidv4()}`;
      const docRef = doc(db, 'courseCategories', newDocId);
      const categoryData = {
        title: categoryName.trim(),
        description: description.trim(),
        [oldDocId ? 'updatedAt' : 'createdAt']: serverTimestamp()
      };
      // If editing and name changes, migrate document
      if (oldDocId && oldDocId !== newDocId) {
        const oldDocRef = doc(db, 'courseCategories', oldDocId);
        const oldDocSnap = await getDoc(oldDocRef);
        await setDoc(docRef, {
          ...oldDocSnap.data(),
          ...categoryData,
          id: newDocId
        });
        await deleteDoc(oldDocRef);
      } else {
        await setDoc(docRef, categoryData);
      }
      // Refresh categories from Firestore
      const querySnapshot = await getDocs(collection(db, 'courseCategories'));
      const updatedCategories = querySnapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().title,
        description: doc.data().description,
        createdAt: doc.data().createdAt
      }));
      setCategories(updatedCategories);
      setNotification({ 
        visible: true, 
        message: `Category ${oldDocId ? 'updated' : 'added'} successfully!`, 
        type: "success" 
      });
      setTimeout(() => setNotification({ visible: false, message: "", type: "" }), 3000);
      setCategoryName("");
      setDescription("");
      setShowForm(false);
      setEditIndex(null);
    } catch (error) {
      console.error("Error saving category: ", error);
      setNotification({ visible: true, message: `Error: ${error.message}`, type: "failure" });
      setTimeout(() => setNotification({ visible: false, message: "", type: "" }), 3000);
    }
  };

  // Prepare form for editing a category
  const handleEditCategory = index => {
    setCategoryName(categories[index].name);
    setDescription(categories[index].description);
    setEditIndex(index);
    setShowForm(true);
  };

  // Show delete confirmation modal
  const handleShowDeleteModal = index => {
    setCategoryToDelete(index);
    setShowDeleteModal(true);
  };

  // Delete a category
  const handleDeleteCategory = async () => {
    try {
      const categoryId = categories[categoryToDelete].id;
      await deleteDoc(doc(db, 'courseCategories', categoryId));
      const newCategories = categories.filter((_, i) => i !== categoryToDelete);
      setCategories(newCategories);
      setNotification({ visible: true, message: "Category deleted successfully!", type: "success" });
      setTimeout(() => setNotification({ visible: false, message: "", type: "" }), 3000);
      setShowDeleteModal(false);
    } catch (error) {
      console.error("Error deleting category: ", error);
      setNotification({ visible: true, message: `Error: ${error.message}`, type: "failure" });
      setTimeout(() => setNotification({ visible: false, message: "", type: "" }), 3000);
    }
  };

  // Fetch categories from Firestore on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'courseCategories'));
        const categoriesData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().title,
          description: doc.data().description,
          createdAt: doc.data().createdAt
        }));
        setCategories(categoriesData);
      } catch (error) {
        console.error("Error fetching categories: ", error);
        setNotification({ visible: true, message: `Error fetching categories: ${error.message}`, type: "failure" });
      }
    };
    fetchCategories();
  }, []);

  return (
    <div className="container-fluid py-4">
      {/* Notification Banner */}
      <Notification message={notification.message} type={notification.type} visible={notification.visible} onClose={() => setNotification({ visible: false, message: "", type: "" })} />
      {/* Header Section */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h2 className="fw-bold mb-1">Course Categories</h2>
          <div className="text-muted">Create and manage course categories</div>
        </div>
        <button
          className="btn btn-dark d-flex align-items-center gap-2"
          onClick={() => {
            setCategoryName("");
            setDescription("");
            setEditIndex(null);
            setShowForm(true);
          }}
        >
          <i className="bi bi-plus-circle"></i> Add Category
        </button>
      </div>
      {/* Main Table Section */}
      <div className="bg-white rounded-3 p-4 shadow-sm border">
        <h4 className="fw-bold mb-1">Category Management</h4>
        <div className="text-muted mb-3">View and manage all course categories</div>
        {/* Search Input */}
        <input
          type="text"
          className="form-control mb-3"
          placeholder="Search categories..."
          value={search}
          onChange={handleSearch}
          style={{ maxWidth: 350 }}
        />
        {/* Categories Table */}
        <div className="table-responsive">
          <table className="table align-middle mb-0">
            <thead>
              <tr>
                <th style={{ width: '150px', textAlign: 'left' }}>Name</th>
                <th style={{ width: '260px', textAlign: 'left' }}>Description</th>
                <th style={{ width: '180px', textAlign: 'center' }}>Created At</th>
                <th style={{ width: '100px', textAlign: 'center' }}></th>
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan="4" className="text-center text-muted">No categories found.</td>
                </tr>
              ) : (
                paginated.map((cat, idx) => (
                  <tr key={idx}>
                    <td style={{ padding: '8px 12px', verticalAlign: 'middle' }}>{cat.name}</td>
                    <td style={{ maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '8px 12px', verticalAlign: 'middle' }}>{cat.description}</td>
                    <td style={{ textAlign: 'center', padding: '8px 12px', verticalAlign: 'middle' }}>{cat.createdAt?.toDate().toLocaleString()}</td>
                    <td style={{ textAlign: 'center', padding: '8px 12px', verticalAlign: 'middle' }}>
                      <button className="btn btn-link p-1" title="Edit" onClick={() => handleEditCategory(idx)}>
                        <i className="bi bi-pencil-square"></i>
                      </button>
                      <button className="btn btn-link p-1 text-danger" title="Delete" onClick={() => handleShowDeleteModal(idx)}>
                        <i className="bi bi-trash3-fill"></i>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {/* Pagination Controls */}
        <div className="d-flex justify-content-between align-items-center mt-3">
          <div className="text-muted small">
            Showing {filtered.length === 0 ? 0 : (page - 1) * itemsPerPage + 1} to {Math.min(page * itemsPerPage, filtered.length)} of {filtered.length} entries
          </div>
          <div className="d-flex align-items-center gap-2">
            <button
              className="btn btn-light btn-sm"
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
            >
              &lt;
            </button>
            <span className="small">Page {page} of {totalPages || 1}</span>
            <button
              className="btn btn-light btn-sm"
              onClick={() => setPage(page + 1)}
              disabled={page === totalPages || totalPages === 0}
            >
              &gt;
            </button>
          </div>
        </div>
      </div>
      {/* Add/Edit Category Modal */}
      {showForm && (
        <div className="modal d-block" tabIndex="-1" style={{ background: "#0002" }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editIndex !== null ? "Edit Category" : "Add Category"}</h5>
                <button type="button" className="btn-close" onClick={() => setShowForm(false)}></button>
              </div>
              <form onSubmit={handleAddCategory}>
                <div className="modal-body">
                  <div className="mb-2">
                    <label className="form-label">Category Name</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g., Web Development"
                      value={categoryName}
                      onChange={e => setCategoryName(e.target.value)}
                      required
                    />
                  </div>
                  <div className="mb-2">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      placeholder="Describe the category..."
                      value={description}
                      onChange={e => setDescription(e.target.value)}
                      rows={2}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                  <button type="submit" className="btn btn-dark">{editIndex !== null ? "Save Changes" : "Add"}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this category?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteCategory}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Category;